import _ from 'underscore';
import { useCallback, useContext } from 'react';
import { createOrUpdateCampaign, readCampaigns, deleteCampaign } from '@bingads-webui-campaign-react/perf-max-data';
import { ScopeContext } from '@bingads-webui-react/scope-service';

import { usePreference, preferenceNames } from './use-preference';
import { useGetDefaultBudgetValueForCountry } from './use-get-budget-default-value-for-country';

export function usePmaxCampaignCRUD({
  onCreateCampaignDone = _.noop,
  onCreateCampaignError = _.noop,
} = {}) {
  const { scope } = useContext(ScopeContext);
  const { cid, aid, campaignId } = scope.values;

  const [{ value: languagePreference }] = usePreference(preferenceNames.SelectedLanguage);
  const { getDefaultBudgetValueForCountry } = useGetDefaultBudgetValueForCountry();

  const readCampaignsPromise = useCallback(() => readCampaigns(cid, aid).then((res) => {
    const campaigns = res.value;
    return Promise.resolve(campaigns);
  }), [aid, cid]);

  const deleteCampaignPromise = useCallback((deleteCampaignId) => {
    if (deleteCampaignId) {
      return deleteCampaign(deleteCampaignId).then(() => {
        scope.remove(['campaignId', 'assetGroupId']);
        scope.history.replace({
          pathname: scope.history.location.pathname,
          search: scope.stringify(),
        });
      });
    }

    return Promise.resolve();
  }, [scope]);

  const createOrUpdateCampaignPromise = useCallback(
    () => getDefaultBudgetValueForCountry().then((defaultBudgetValue) => {
      const campaignData = {
        Name: `Performance-Max-${Math.floor(Math.random() * 100)}`,
        Budget: {
          Amount: defaultBudgetValue,
          Type: 'DailyStandard',
        },
        Status: 'Active',
        TimeZone: 'PacificTimeUSCanadaTijuana',
        CampaignType: 'PerformanceMax',
        BiddingScheme: {
          Type: 'MaxConversions', // Enhanced CPC will be supported for Performance Max campaigns in Q1
        },
        Languages: [languagePreference],
      };

      let campaignPromise = () => Promise.resolve();
      if (!campaignId) {
        campaignPromise = () => createOrUpdateCampaign(campaignData).then((campaign) => {
          scope.append({ campaignId: campaign.Id });
          scope.history.replace({
            pathname: scope.history.location.pathname,
            search: scope.stringify(),
          });
          return campaign;
        });
      }

      return campaignPromise()
        .then((campaign) => {
          onCreateCampaignDone();
          return campaign;
        })
        .catch((error) => {
          onCreateCampaignError(error);
          throw new Error(error);
        });
    }),
    [campaignId, getDefaultBudgetValueForCountry, languagePreference,
      onCreateCampaignDone, onCreateCampaignError, scope]
  );

  return {
    createOrUpdateCampaignPromise,
    deleteCampaignPromise,
    readCampaignsPromise,
  };
}
